/**
 * SwapAnalytics Component
 *
 * Displays comprehensive analytics about swap operations:
 * - Total swap count
 * - Total volume swapped
 * - Average performance per swap
 * - Best/worst swaps
 * - Swap timeline chart
 * - Chain breakdown (cross-chain stats)
 *
 * Only visible in expert mode.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';
import { getSwapChain } from '../../utils/costBasisTracking';
import type { Token, Position, Prices } from '../../types';

interface SwapAnalyticsProps {
  tokens: Token[];
  prices: Prices;
  isExpertMode?: boolean;
}

interface SwapStats {
  totalSwaps: number;
  totalVolumeUsd: number;
  totalFeesUsd: number;
  avgPerformance: number;
  bestSwap: SwapInfo | null;
  worstSwap: SwapInfo | null;
  crossChainCount: number;
  sameChainCount: number;
  chainBreakdown: Record<string, number>;
  platformBreakdown: Record<string, number>;
  monthlyVolume: Record<string, number>;
}

interface SwapInfo {
  fromToken: string;
  toToken: string;
  date: string;
  performance: number;
  volumeUsd: number;
  isCrossChain: boolean;
}

export function SwapAnalytics({ tokens, prices, isExpertMode = false }: SwapAnalyticsProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  // Calculate swap statistics
  const stats = useMemo<SwapStats>(() => {
    const swapPositions: { position: Position; token: Token }[] = [];

    // Collect all swap positions
    tokens.forEach(token => {
      token.positions
        .filter(p => p.is_swap === true)
        .forEach(p => swapPositions.push({ position: p, token }));
    });

    if (swapPositions.length === 0) {
      return {
        totalSwaps: 0,
        totalVolumeUsd: 0,
        totalFeesUsd: 0,
        avgPerformance: 0,
        bestSwap: null,
        worstSwap: null,
        crossChainCount: 0,
        sameChainCount: 0,
        chainBreakdown: {},
        platformBreakdown: {},
        monthlyVolume: {},
      };
    }

    let totalVolumeUsd = 0;
    let totalFeesUsd = 0;
    let totalPerformance = 0;
    let crossChainCount = 0;
    let sameChainCount = 0;
    const chainBreakdown: Record<string, number> = {};
    const platformBreakdown: Record<string, number> = {};
    const monthlyVolume: Record<string, number> = {};
    const swapInfos: SwapInfo[] = [];

    swapPositions.forEach(({ position, token }) => {
      const currentPrice = prices[token.id]?.usd || position.price;
      const volumeUsd = position.quantity * position.price;
      const currentValue = position.quantity * currentPrice;
      const originalCost = position.original_cost_basis
        ? position.quantity * position.original_cost_basis
        : volumeUsd;
      const performance = originalCost > 0
        ? ((currentValue - originalCost) / originalCost) * 100
        : 0;

      totalVolumeUsd += volumeUsd;
      totalPerformance += performance;

      // Cross-chain stats
      const isCrossChain = !!(position.source_chain && position.destination_chain &&
        position.source_chain !== position.destination_chain);
      if (isCrossChain) {
        crossChainCount++;
        // Chain breakdown
        if (position.source_chain) {
          chainBreakdown[position.source_chain] = (chainBreakdown[position.source_chain] || 0) + 1;
        }
        if (position.destination_chain) {
          chainBreakdown[position.destination_chain] = (chainBreakdown[position.destination_chain] || 0) + 1;
        }
      } else {
        sameChainCount++;
      }

      // Platform breakdown
      if (position.swap_platform) {
        platformBreakdown[position.swap_platform] = (platformBreakdown[position.swap_platform] || 0) + 1;
      }

      // Fees
      totalFeesUsd += (position.bridge_fee || 0) + (position.gas_fee_usd || 0);

      // Monthly volume
      const month = position.date.substring(0, 7); // YYYY-MM
      monthlyVolume[month] = (monthlyVolume[month] || 0) + volumeUsd;

      // Swap info for best/worst
      swapInfos.push({
        fromToken: 'Previous',
        toToken: token.displayName || token.id,
        date: position.date,
        performance,
        volumeUsd,
        isCrossChain,
      });
    });

    // Find best and worst swaps
    const sortedByPerformance = [...swapInfos].sort((a, b) => b.performance - a.performance);
    const bestSwap = sortedByPerformance[0] || null;
    const worstSwap = sortedByPerformance[sortedByPerformance.length - 1] || null;

    return {
      totalSwaps: swapPositions.length,
      totalVolumeUsd,
      totalFeesUsd,
      avgPerformance: swapPositions.length > 0 ? totalPerformance / swapPositions.length : 0,
      bestSwap,
      worstSwap,
      crossChainCount,
      sameChainCount,
      chainBreakdown,
      platformBreakdown,
      monthlyVolume,
    };
  }, [tokens, prices]);

  // Don't show if no swaps or not in expert mode
  if (!isExpertMode || stats.totalSwaps === 0) {
    return null;
  }

  return (
    <div style={{
      padding: '20px',
      background: 'var(--card)',
      borderRadius: '12px',
      border: '1px solid var(--border)',
      marginBottom: '20px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.BarChart size={20} style={{ color: 'white' }} />
          </div>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '2px' }}>
              {t('swapAnalytics.title', 'Swap Analytics')}
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('swapAnalytics.subtitle', 'Statistiques de vos opérations de swap')}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {showDetails ? <Icons.ChevronUp size={14} /> : <Icons.ChevronDown size={14} />}
          {showDetails ? t('common.hide', 'Masquer') : t('common.details', 'Détails')}
        </button>
      </div>

      {/* Main Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginBottom: showDetails ? '20px' : '0',
      }}>
        {/* Total Swaps */}
        <div style={{
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('swapAnalytics.totalSwaps', 'Total Swaps')}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--accent)' }}>
            {stats.totalSwaps}
          </div>
        </div>

        {/* Total Volume */}
        <div style={{
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('swapAnalytics.totalVolume', 'Volume Total')}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>
            ${fmt(stats.totalVolumeUsd, 0)}
          </div>
        </div>

        {/* Average Performance */}
        <div style={{
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('swapAnalytics.avgPerformance', 'Perf. Moyenne')}
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: stats.avgPerformance >= 0 ? '#22c55e' : '#ef4444',
          }}>
            {stats.avgPerformance >= 0 ? '+' : ''}{fmt(stats.avgPerformance, 1)}%
          </div>
        </div>

        {/* Total Fees */}
        <div style={{
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('swapAnalytics.totalFees', 'Frais Totaux')}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>
            ${fmt(stats.totalFeesUsd, 2)}
          </div>
        </div>
      </div>

      {/* Detailed Stats (Expandable) */}
      {showDetails && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Cross-Chain Stats */}
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
            borderRadius: '8px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Icons.ArrowRightLeft size={14} style={{ color: 'var(--accent)' }} />
              {t('swapAnalytics.crossChainStats', 'Statistiques Cross-Chain')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Same-Chain</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>{stats.sameChainCount}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Cross-Chain</div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#8b5cf6' }}>{stats.crossChainCount}</div>
              </div>
            </div>

            {/* Chain Breakdown */}
            {Object.keys(stats.chainBreakdown).length > 0 && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '8px' }}>
                  {t('swapAnalytics.chainBreakdown', 'Répartition par chaîne')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {Object.entries(stats.chainBreakdown).map(([chain, count]) => (
                    <span
                      key={chain}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: 'var(--bg)',
                        fontSize: '11px',
                      }}
                    >
                      {chain}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Platform Breakdown */}
          {Object.keys(stats.platformBreakdown).length > 0 && (
            <div style={{
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icons.Globe size={14} />
                {t('swapAnalytics.platformBreakdown', 'Plateformes utilisées')}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(stats.platformBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([platform, count]) => (
                    <div
                      key={platform}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        fontSize: '12px',
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{platform}</span>
                      <span style={{ color: 'var(--muted)', marginLeft: '6px' }}>({count})</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Best & Worst Swaps */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* Best Swap */}
            {stats.bestSwap && (
              <div style={{
                padding: '16px',
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(34, 197, 94, 0.2)',
              }}>
                <div style={{ fontSize: '11px', color: '#22c55e', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Icons.TrendingUp size={12} />
                  {t('swapAnalytics.bestSwap', 'Meilleur Swap')}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                  {stats.bestSwap.toToken}
                </div>
                <div style={{ fontSize: '13px', color: '#22c55e', fontWeight: 600 }}>
                  +{fmt(stats.bestSwap.performance, 1)}%
                </div>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                  {new Date(stats.bestSwap.date).toLocaleDateString('fr-FR')} • ${fmt(stats.bestSwap.volumeUsd, 0)}
                </div>
              </div>
            )}

            {/* Worst Swap */}
            {stats.worstSwap && stats.worstSwap.performance < 0 && (
              <div style={{
                padding: '16px',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}>
                <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Icons.TrendingDown size={12} />
                  {t('swapAnalytics.worstSwap', 'Pire Swap')}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                  {stats.worstSwap.toToken}
                </div>
                <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: 600 }}>
                  {fmt(stats.worstSwap.performance, 1)}%
                </div>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                  {new Date(stats.worstSwap.date).toLocaleDateString('fr-FR')} • ${fmt(stats.worstSwap.volumeUsd, 0)}
                </div>
              </div>
            )}
          </div>

          {/* Monthly Volume */}
          {Object.keys(stats.monthlyVolume).length > 0 && (
            <div style={{
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icons.Calendar size={14} />
                {t('swapAnalytics.monthlyVolume', 'Volume mensuel')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(stats.monthlyVolume)
                  .sort((a, b) => b[0].localeCompare(a[0]))
                  .slice(0, 6)
                  .map(([month, volume]) => {
                    const maxVolume = Math.max(...Object.values(stats.monthlyVolume));
                    const percentage = (volume / maxVolume) * 100;
                    return (
                      <div key={month} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '70px', fontSize: '12px', color: 'var(--muted)' }}>
                          {month}
                        </div>
                        <div style={{ flex: 1, height: '8px', background: 'var(--bg)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: '100%',
                            background: 'var(--accent)',
                            borderRadius: '4px',
                          }} />
                        </div>
                        <div style={{ width: '80px', fontSize: '12px', fontWeight: 600, textAlign: 'right' }}>
                          ${fmt(volume, 0)}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
